// La portada del Catálogo en dos pasos (ADR-2026-10-13-catalogo-con-el-agente,
// ADR-2026-10-14-el-catalogo-arranca-con-el-agente-a-pantalla-completa):
//  1. Elegir los productos, con el selector de los modos de pantalla completa (el de Ambiente
//     completo): buscador y grilla. El pie es el del selector del agente ("Elegí qué agregar"):
//     las miniaturas de lo elegido a la izquierda —cada foto tocada VUELA hasta la suya— y
//     "Siguiente" a la derecha (ADR-2026-10-14-el-pie-de-elegir-productos-es-el-del-agente).
//     Ese paso es `ElegirProductosDeSesion` (shared), el mismo que usa Videos cortos
//     (ADR-2026-10-30-videos-cortos-con-el-agente).
//  2. El agente a pantalla completa (`IntroDelCatalogo`): Cumbre pregunta cómo se quiere el
//     catálogo. Mandar crea la sesión (un chat de tipo `catalogo`), guarda el plan vacío con los
//     productos y sus datos, carga ese chat y abre la sesión, que sigue con la conversación a
//     pantalla completa hasta que el agente arma el catálogo
//     (ADR-2026-10-14-el-catalogo-conversa-a-pantalla-completa-hasta-armar). La sesión le manda el
//     pedido al agente.
import { useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useChatAtlas } from '../../../../chat-atlas/ChatAtlasContext';
import type { ChatAttachment } from '../../agentAttachments';
import { TIPO_CHAT_CATALOGO, createChat, listSesionesCatalogo, type SesionDeCatalogoResumen } from '../../agentChatApi';
import { useAgentEnv } from '../../agentEnvContext';
import { useSidebar } from '../../../../context/SidebarContext';
import { fetchFichaPorVariante, fetchMedidasPorVariante } from '../../medidasApi';
import type { MockProductV2 } from '../../mockData';
import { ElegirProductosDeSesion, ListaDeSesiones } from '../shared/ElegirProductosDeSesion';
import { IntroDelCatalogo } from './IntroDelCatalogo';
import { MAX_PRODUCTOS, planVacio, type PlanDeCatalogo, type ProductoParaElPlan, type ReferenciaDelPlan } from './plan';
import { guardarPlan } from './planGuardado';
import { RUTA_CATALOGO, rutaDeSesion } from './rutas';
import './transicionDelCatalogo.css';

/** Lo que la portada le deja a la sesión para que mande el primer mensaje al agente. */
export interface PrimerMensaje {
  texto: string;
  adjuntos: ChatAttachment[];
}

/** El `location.state` con que la portada abre la sesión: el pedido y el plan recién guardado
 *  (así la sesión dibuja el catálogo en el primer frame, sin esperar a leerlo). */
export interface EstadoDeLaPortada {
  primerMensaje: PrimerMensaje;
  plan: PlanDeCatalogo;
}

const referenciasDe = (adjuntos: ChatAttachment[]): ReferenciaDelPlan[] =>
  adjuntos
    .filter((a): a is Extract<ChatAttachment, { kind: 'image' }> => a.kind === 'image')
    .map((a, i) => ({ id: a.id, etiqueta: `Referencia ${i + 1}`, storagePath: a.storagePath, ...(a.bucket ? { bucket: a.bucket } : {}) }));

const pideMenosMovimiento = (): boolean => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

export const PortadaDeCatalogo: React.FC<{
  rubro: string | null;
  /** Se dio de alta un producto: el host vuelve a traer el catálogo de la marca. */
  onProductosNuevos?: () => void;
}> = ({ rubro, onProductosNuevos }) => {
  const navigate = useNavigate();
  const { userId, companyId, brandId } = useAgentEnv();
  const { cargarChat } = useChatAtlas();
  const [paso, setPaso] = useState<'productos' | 'pedido'>('productos');
  // En el teléfono el botón de atrás del shell retrocede DE PASO, como en Ambiente completo: de la
  // pregunta vuelve a los productos (sin perder lo elegido) y sólo desde la grilla sale del modo.
  // Sin esto hacía `history.back()` y la pregunta, que es un paso de la misma ruta, sacaba del
  // Catálogo (ADR-2026-10-15-el-catalogo-en-el-telefono-3).
  const { setMobileBackOverride } = useSidebar();
  useEffect(() => {
    setMobileBackOverride(() => () => {
      if (paso === 'pedido') setPaso('productos');
      else navigate('/interfaz-v2');
    });
    return () => setMobileBackOverride(null);
  }, [paso, setMobileBackOverride, navigate]);
  const [elegidos, setElegidos] = useState<MockProductV2[]>([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sesiones, setSesiones] = useState<SesionDeCatalogoResumen[]>([]);
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    void listSesionesCatalogo().then((s) => {
      if (vivo.current) setSesiones(s);
    });
    return () => {
      vivo.current = false;
    };
  }, []);

  const motivoBloqueo =
    elegidos.length === 0
      ? 'Elegí al menos un producto.'
      : elegidos.length > MAX_PRODUCTOS
        ? `Hasta ${MAX_PRODUCTOS} productos por catálogo (elegiste ${elegidos.length}).`
        : null;

  const enviar = useCallback(
    async (texto: string, adjuntos: ChatAttachment[]) => {
      if (!userId || !companyId || motivoBloqueo || enviando) return;
      setEnviando(true);
      setError(null);
      try {
        const ids = elegidos.map((p) => p.id);
        const [medidas, fichas] = await Promise.all([fetchMedidasPorVariante(ids), fetchFichaPorVariante(ids)]);
        const productos: ProductoParaElPlan[] = elegidos.map((p) => ({
          varianteId: p.id,
          nombre: p.name,
          ...(p.imageUrl ? { portadaUrl: p.imageUrl } : {}),
          medidas: medidas[p.id] ?? null,
          ficha: fichas[p.id] ?? { campos: [] },
        }));
        const plan = planVacio(productos, referenciasDe(adjuntos), texto);
        const id = await createChat({ userId, companyId, title: plan.titulo, messages: [], tipo: TIPO_CHAT_CATALOGO });
        if (!id) throw new Error('No se pudo crear la sesión.');
        await guardarPlan(id, plan);
        // El chat se carga ANTES de abrir la sesión: si no, la conversación mostraría un instante
        // la que estaba abierta antes.
        await cargarChat(id);
        if (!vivo.current) return;
        const estado: EstadoDeLaPortada = { primerMensaje: { texto, adjuntos }, plan };
        const abrir = () => navigate(rutaDeSesion(id), { state: estado });
        const doc = document as Document & { startViewTransition?: (cb: () => void) => unknown };
        if (doc.startViewTransition && !pideMenosMovimiento()) doc.startViewTransition(() => flushSync(abrir));
        else abrir();
      } catch (e) {
        setError(e instanceof Error ? e.message : 'No se pudo abrir la sesión. Probá de nuevo.');
        setEnviando(false);
      }
    },
    [userId, companyId, motivoBloqueo, enviando, elegidos, cargarChat, navigate],
  );

  if (paso === 'pedido') {
    return <IntroDelCatalogo productos={elegidos} enviando={enviando} error={error} onEnviar={(t, a) => void enviar(t, a)} onVolver={() => setPaso('productos')} />;
  }

  // El paso 1 es el de elegir productos de las sesiones con el agente, compartido con Videos cortos.
  return (
    <ElegirProductosDeSesion
      titulo="Elegí los productos para el catálogo"
      migas={[{ label: 'Inicio', onClick: () => navigate('/interfaz-v2') }, { label: 'Catálogo', onClick: () => navigate(RUTA_CATALOGO) }]}
      maxProductos={MAX_PRODUCTOS}
      unidad="catálogo"
      derecha={<ListaDeSesiones etiqueta="Mis catálogos" sesiones={sesiones} onAbrir={(id) => navigate(rutaDeSesion(id))} />}
      rubro={rubro}
      companyId={companyId}
      brandId={brandId ?? null}
      onProductosNuevos={onProductosNuevos}
      elegidos={elegidos}
      onElegidos={setElegidos}
      motivoBloqueo={motivoBloqueo}
      onSiguiente={() => setPaso('pedido')}
      onSalir={() => navigate('/interfaz-v2')}
    />
  );
};
